import { useState, useCallback, useRef, useEffect } from 'react';
import { ApiResponse } from '../types';

export const usePaginatedQuery = <T>(
  fetchFn: (lastDoc?: any) => Promise<ApiResponse<{ items: T[], lastDoc: any, hasMore: boolean }>>,
) => {
  const [items, setItems] = useState<T[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Usa ref para evitar recriação do callback e reduzir leituras desnecessárias
  const lastDocRef = useRef<any>(null);
  const isMountedRef = useRef(true);
  const loadingRef = useRef(false);
  const fetchFnRef = useRef(fetchFn);

  // Atualiza ref quando fetchFn muda
  useEffect(() => {
    fetchFnRef.current = fetchFn;
  }, [fetchFn]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const loadMore = useCallback(async () => {
    // Previne chamadas duplicadas simultâneas
    if (loadingRef.current) {
      return;
    }
    
    loadingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const res = await fetchFnRef.current(lastDocRef.current);
      
      // Só atualiza state se o componente ainda está montado
      if (!isMountedRef.current) return;

      if (res.success && res.data) {
        const data = res.data;
        setItems(prev => [...prev, ...data.items]);
        lastDocRef.current = data.lastDoc;
        setHasMore(data.hasMore);
      } else {
        setError(res.error || 'Erro ao carregar dados');
        setHasMore(false);
      }
    } catch (err) {
      if (isMountedRef.current) {
        setError('Erro ao carregar dados');
        setHasMore(false);
        console.error('Erro no usePaginatedQuery:', err);
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
        loadingRef.current = false;
      }
    }
  }, []); // Array vazio - função nunca é recriada!

  // Função separada para reset - evita leituras duplicadas
  const reset = useCallback(async () => {
    // Previne múltiplos resets simultâneos
    if (loadingRef.current) {
      return;
    }
    
    lastDocRef.current = null;
    setItems([]);
    setHasMore(true);
    setError(null);
    loadingRef.current = false;
    
    // Carrega a primeira página
    await loadMore();
  }, [loadMore]);

  return { 
    items, 
    loadMore, 
    reset, 
    hasMore, 
    loading, 
    error 
  };
};
